import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Type } from '@angular/core';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { ErrorMessageHelper } from '@lsq-frontend/common-ui';
import { ColumnData } from './models/column-data';
import { InvoiceTotals } from './models/invoice-totals';
import { ReportType, FilterBy, ReportStatus, DateRange } from './models/reports.constants';
import { ReportCenterApiService } from './report-center-api';

@Injectable()
export class GenerateReportPreviewService extends ReportCenterApiService<
    GenerateReportPreviewRequest,
    GenerateReportPreviewResponse
> {
    public requestType: Type<GenerateReportPreviewRequest>;
    public responseType: Type<GenerateReportPreviewResponse>;
    public endpointName = 'v1/generateReportPreview';

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _alertService: AlertService,
        private _errorHelper: ErrorMessageHelper
    ) {
        super(http, settings);
    }

    public executeOnce(
        request: GenerateReportPreviewRequest
    ): Promise<GenerateReportPreviewResponse> {
        return this._http
            .post<GenerateReportPreviewResponse>(this.endpointUrl, request)
            .toPromise()
            .then((response) =>
                response !== undefined
                    ? Promise.resolve(new GenerateReportPreviewResponse(response))
                    : Promise.reject(null)
            )
            .catch((err) => {
                this._alertService.error(this._errorHelper.GENERIC_ERROR);
                return Promise.reject(err);
            });
    }
}

export class GenerateReportPreviewRequest {
    public id: number;
    public name: string;
    public companyRoleId: number;
    public reportType: ReportType;
    public filterBy: FilterBy;
    public status: ReportStatus;
    public dateRange: DateRange;
    public startDate?: string;
    public endDate?: string;
    public enableFooterTotals?: boolean;

    constructor(obj?: Partial<GenerateReportPreviewRequest>) {
        this.id = obj?.id || -1;
        this.name = obj?.name || '';
        this.companyRoleId = obj?.companyRoleId || -1;
        this.reportType = obj?.reportType || null;
        this.filterBy = obj?.filterBy || null;
        this.status = obj?.status || ReportStatus.All;
        this.dateRange = obj?.dateRange || null;
        this.startDate = obj?.startDate || null;
        this.endDate = obj?.endDate || null;
        this.enableFooterTotals = obj?.enableFooterTotals || false;
    }
}

export class GenerateReportPreviewResponse {
    public ok: boolean;
    public name: string;
    public dateRangeDisplay: string;
    public filterBy: FilterBy;
    public status: ReportStatus;
    public reportType: ReportType;
    public totals: InvoiceTotals;
    public columns: ColumnData[];
    public data: object[][];

    constructor(obj?: Partial<GenerateReportPreviewResponse>) {
        this.ok = true;
        this.name = obj?.name;
        this.dateRangeDisplay = obj?.dateRangeDisplay || '-';
        this.filterBy = obj?.filterBy || null;
        this.status = obj?.status || ReportStatus.All;
        this.reportType = obj?.reportType || null;
        this.totals = new InvoiceTotals(obj?.totals);
        this.columns = obj?.columns || [];
        this.data = obj?.data || [];
    }
}
